import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

import { apiRequest, setCsrfToken } from "@mhj/shared/api";
import { normalisePhone } from "@mhj/shared/phone";
import { useAuth } from "@mhj/shared/auth";
import { Card, Field, buttonClass, inputClass } from "../lib/ui";

export default function Register() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    password: "",
  });

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};

    if (form.fullName.trim().length < 2) next.fullName = "Enter your full name";
    // Validate here as well as server-side so a mistyped number is caught
    // before it costs the applicant a round trip on a weak connection.
    if (!normalisePhone(form.phone)) {
      next.phone = "Enter a valid Nigerian phone number, for example 08031234567";
    }
    if (form.password.length < 8) next.password = "Use at least 8 characters";

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setBusy(true);
    try {
      const response = await apiRequest<{ csrfToken?: string }>("/auth/register", {
        method: "POST",
        data: {
          fullName: form.fullName.trim(),
          phone: normalisePhone(form.phone),
          email: form.email.trim() || null,
          password: form.password,
        },
      });
      if (response.csrfToken) setCsrfToken(response.csrfToken);
      await refresh();
      toast.success("Account created. We sent a code to your phone.");
      navigate(`/verify-phone?phone=${encodeURIComponent(normalisePhone(form.phone)!)}`);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card
        title="Create your account"
        description="You need a phone number that can receive text messages. An email address is optional."
      >
        <form className="space-y-4" onSubmit={submit}>
          <Field label="Full name" required error={errors.fullName}>
            <input
              className={inputClass}
              value={form.fullName}
              onChange={set("fullName")}
              autoComplete="name"
              placeholder="Aisha Bello"
            />
          </Field>

          <Field
            label="Phone number"
            required
            error={errors.phone}
            hint="We send your application ID and your result to this number."
          >
            <input
              className={inputClass}
              value={form.phone}
              onChange={set("phone")}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="08031234567"
            />
          </Field>

          <Field label="Email address" hint="Optional. Leave blank if you do not have one.">
            <input
              className={inputClass}
              value={form.email}
              onChange={set("email")}
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
            />
          </Field>

          <Field label="Password" required error={errors.password} hint="At least 8 characters.">
            <input
              className={inputClass}
              value={form.password}
              onChange={set("password")}
              type="password"
              autoComplete="new-password"
            />
          </Field>

          <button type="submit" className={buttonClass} disabled={busy}>
            {busy ? "Creating your account…" : "Create account"}
          </button>
        </form>
      </Card>

      <p className="text-center text-sm text-slate-600">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-blue-700 underline">
          Sign in
        </Link>
      </p>
      <p className="text-center text-sm text-slate-600">
        An officer registered you?{" "}
        <Link to="/claim" className="font-medium text-blue-700 underline">
          Use your code
        </Link>
      </p>
    </div>
  );
}
